function generateSeedOrder(n) {
    const order = [1];
    while (order.length < n) {
        const size = order.length * 2;
        const next = [];
        for (let i = 0; i < order.length; i++) {
            next.push(order[i]);
            next.push(size + 1 - order[i]);
        }
        order.splice(0, order.length, ...next);
    }
    return order;
}

export function buildSingleEliminationBracket(players) {
    const rounds = Math.ceil(Math.log2(players.length));
    const slots = 2 ** rounds;
    const seedOrder = generateSeedOrder(slots);
    const seeded = Array(slots).fill("BYE");

    // Seed players according to proper tournament seeding
    for (let i = 0; i < players.length; i++) {
        seeded[seedOrder[i] - 1] = players[i];
    }

    const matches = [];
    let matchNum = 1;
    let prevRound = [];

    // Build all rounds of single elimination bracket
    for (let r = 1; r <= rounds; r++) {
        const thisRound = [];
        const count = slots / (2 ** r);

        for (let i = 0; i < count; i++) {
            let p1, p2, from = [];

            if (r === 1) {
                // First round - direct players
                p1 = { name: seeded[i * 2], ref: null };
                p2 = { name: seeded[i * 2 + 1], ref: null };
            } else {
                // Later rounds - references to previous matches
                const m1 = prevRound[i * 2];
                const m2 = prevRound[i * 2 + 1];
                
                p1 = m1 ? { name: `Winner of M${m1.matchNum}`, ref: `Winner of M${m1.matchNum}` } : { name: "BYE", ref: null };
                p2 = m2 ? { name: `Winner of M${m2.matchNum}`, ref: `Winner of M${m2.matchNum}` } : { name: "BYE", ref: null };
                
                if (m1) from.push(m1.id);
                if (m2) from.push(m2.id);
            }

            // Skip completely bye matches
            if (p1.name === "BYE" && p2.name === "BYE") {
                thisRound.push(null);
                continue;
            }

            const match = {
                id: `se-${r}-${i}`,
                matchNum: matchNum++,
                p1,
                p2,
                from,
                bracket: "SE", // Single Elimination
                round: r,
                col: r,
                row: i,
                score1: "0",
                score2: "0",
                table: "",
                started: false,
                finished: false,
                status: "Pending",
                winner: "",
            };


            thisRound.push(match);
            matches.push(match);
        }

        prevRound = thisRound;
    }

    return matches;
}

export function getSingleEliminationStandings(bracket) {
    const places = [];
    const placed = new Set();

    // Sort matches by round (descending) to get elimination order
    const finishedMatches = bracket
        .filter(m => m.finished && m.winner)
        .sort((a, b) => b.round - a.round);

    // Find the winner (final match winner)
    const finalMatch = finishedMatches.find(m => m.round === Math.max(...finishedMatches.map(m => m.round)));
    if (finalMatch && finalMatch.winner && !finalMatch.winner.includes("Winner of")) {
        places.push([finalMatch.winner]);
        placed.add(finalMatch.winner);
        
        // Runner-up is the loser of the final
        const runnerUp = finalMatch.p1.name === finalMatch.winner ? finalMatch.p2.name : finalMatch.p1.name;
        if (runnerUp && !runnerUp.includes("Winner of") && !placed.has(runnerUp)) {
            places.push([runnerUp]);
            placed.add(runnerUp);
        }
    }

    // Group players by elimination round (later rounds = better placement)
    const elimByRound = {};
    finishedMatches.forEach(match => {
        if (match.winner) {
            const loser = match.p1.name === match.winner ? match.p2.name : match.p1.name;
            if (loser && !loser.includes("Winner of") && loser !== "BYE" && !placed.has(loser)) {
                if (!elimByRound[match.round]) {
                    elimByRound[match.round] = [];
                }
                elimByRound[match.round].push(loser);
            }
        }
    });

    // Add eliminated players by round (higher rounds first = better placement)
    const elimRounds = Object.keys(elimByRound).map(Number).sort((a, b) => b - a);
    for (const round of elimRounds) {
        const eliminated = elimByRound[round].filter(name => !placed.has(name));
        if (eliminated.length > 0) {
            places.push(eliminated);
            eliminated.forEach(name => placed.add(name));
        }
    }

    // Add any remaining unplaced players
    const allPlayers = new Set();
    bracket.forEach(match => {
        [match.p1, match.p2].forEach(player => {
            if (player?.name && 
                player.name !== "BYE" && 
                !player.name.startsWith("Winner of") && 
                typeof player.name === "string") {
                allPlayers.add(player.name);
            }
        });
    });

    const neverPlaced = Array.from(allPlayers).filter(name => !placed.has(name));
    if (neverPlaced.length > 0) {
        places.push(neverPlaced);
    }

    return places;
}

export function propagateSingleEliminationWinners(bracket) {
    // Sort matches by match number to ensure proper propagation order
    const sortedMatches = [...bracket].sort((a, b) => (a.matchNum || 0) - (b.matchNum || 0));

    for (const match of sortedMatches) {
        if (match.finished && match.winner) {
            const winner = match.winner;

            // Update all subsequent matches
            for (const m of bracket) {
                if (m.p1?.ref === `Winner of M${match.matchNum}`) {
                    m.p1.name = winner;
                }
                if (m.p2?.ref === `Winner of M${match.matchNum}`) {
                    m.p2.name = winner;
                }
            }
        }
    }
}

export function finalizeSingleEliminationByeMatches(bracket, optionalCallback) {
    // Recursively auto-advance BYE matches until no more can be advanced
    let advancedAny;
    let iterationCount = 0;
    const maxIterations = 20; // Safety limit to prevent infinite loops

    do {
        advancedAny = false;
        iterationCount++;

        // First, propagate any winners from completed matches
        propagateSingleEliminationWinners(bracket);

        // Check if propagation created new BYE vs BYE scenarios from completed matches
        bracket.forEach(match => {
            if (match.finished && match.status.includes("Auto-advanced") &&
                match.p1.name === "BYE" && match.p2.name === "BYE" &&
                match.winner !== "BYE") {
                // This match was completed as bye_vs_reference but propagation made it BYE vs BYE
                // Reset it to be processed correctly as BYE vs BYE
                match.finished = false;
                match.winner = "";
                match.status = "Pending";
            }
        });

        // Then check for new BYE matches to auto-advance in round order
        const sortedMatches = [...bracket].sort((a, b) => a.round - b.round);

        for (const match of sortedMatches) {
            if (!match.finished) {
                const shouldAdvance = shouldAutoAdvanceBye(match);

                if (shouldAdvance) {
                    advancedAny = true;

                    if (shouldAdvance.type === "bye_vs_bye") {
                        // BYE vs BYE: winner is BYE
                        match.winner = "BYE";
                        match.finished = true;
                        match.status = "Auto-advanced (BYE vs BYE)";
                    } else if (shouldAdvance.type === "player_vs_bye") {
                        // Named player vs BYE: player wins
                        match.winner = shouldAdvance.winner;
                        match.finished = true;
                        match.status = "Auto-advanced (BYE)";
                    } else if (shouldAdvance.type === "bye_vs_reference") {
                        // BYE vs Winner/Loser of MX: Winner/Loser reference wins
                        match.winner = shouldAdvance.winner;
                        match.finished = true;
                        match.status = "Auto-advanced (BYE)";
                    }

                    if (optionalCallback) {
                        optionalCallback(match);
                    }
                }
            }
        }

        // Safety check to prevent infinite loops
        if (iterationCount >= maxIterations) {
            console.warn(`finalizeSingleEliminationByeMatches: Reached maximum iterations (${maxIterations}), stopping to prevent infinite loop`);
            break;
        }

    } while (advancedAny);

    // Final propagation to ensure all winners are updated
    propagateSingleEliminationWinners(bracket);
}

// Helper function to determine if a match should auto-advance due to BYE
function shouldAutoAdvanceBye(match) {
    const p1Name = match.p1?.name || "";
    const p2Name = match.p2?.name || "";

    // Case 1: BYE vs BYE - auto-advance with BYE as winner
    if (p1Name === "BYE" && p2Name === "BYE") {
        return { type: "bye_vs_bye" };
    }

    // Case 2: Must have exactly one BYE and one other player
    const hasBye = p1Name === "BYE" || p2Name === "BYE";
    if (!hasBye) return false;

    // Get the non-BYE player name
    const nonByeName = p1Name === "BYE" ? p2Name : p1Name;

    // Case 3: BYE vs Winner/Loser of MX - auto-advance the reference
    if (nonByeName.startsWith("Winner of") || nonByeName.startsWith("Loser of")) {
        return { type: "bye_vs_reference", winner: nonByeName };
    }

    // Don't auto-advance if non-BYE name is empty or just whitespace
    if (!nonByeName || nonByeName.trim() === "") {
        return false;
    }

    // Case 4: Named player vs BYE - auto-advance the named player
    return { type: "player_vs_bye", winner: nonByeName };
}